
import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SYSTEM_VARS_CACHE_KEY } from './useTemplateCache';

export function useSystemVariables() {
  const [systemVariables, setSystemVariables] = useState<Record<string, string>>({});
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);
  
  const loadSystemVariables = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      setLoading(true);
      
      // Check auth session first
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        console.warn('No auth session found while loading system variables');
        throw new Error('Authentication required to load system variables');
      }
      
      // Try to use cached system variables first
      const cachedVars = sessionStorage.getItem(SYSTEM_VARS_CACHE_KEY);
      if (cachedVars) {
        console.log('Using cached system variables');
        setSystemVariables(JSON.parse(cachedVars));
        setLoading(false);
        return;
      }
      
      console.log('Fetching system variables from database');
      const { data, error } = await supabase
        .from('system_variables')
        .select('name, value');
      
      if (error) throw error;
      
      const varsMap: Record<string, string> = {};
      data?.forEach(variable => {
        varsMap[variable.name] = variable.value;
      });
      
      console.log(`Loaded ${data?.length || 0} system variables`);
      
      // Cache the result
      try {
        sessionStorage.setItem(SYSTEM_VARS_CACHE_KEY, JSON.stringify(varsMap));
      } catch (e) {
        console.log('Failed to cache system variables:', e);
      }
      
      if (isMountedRef.current) {
        setSystemVariables(varsMap);
      }
    } catch (error: any) {
      console.error('Error loading system variables:', error);
      setError(error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  return {
    systemVariables,
    loadSystemVariables,
    loading,
    error
  };
}
